<script lang="ts">
  import AuthLayout from "../auth/AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import { syncEntitlements } from "$lib/api/endpoints";
  import { AppError } from "$lib/api/problem";
  import { session } from "$lib/stores/session.svelte";
  import { router } from "../../router.svelte";

  // A verified account whose email has no matching entitlement yet. This is a normal
  // resting state (account creation is never blocked on a purchase): purchases are
  // discovered automatically by verified email, so the primary action is a re-check.
  let checking = $state(false);
  let checked = $state(false);
  let error = $state<AppError | null>(null);

  async function checkAgain(): Promise<void> {
    checking = true;
    error = null;
    try {
      const result = await syncEntitlements();
      if (result.entitlementCount > 0) {
        await session.refresh();
        if (session.organizations.length > 0) {
          router.navigate("/", { replace: true });
          return;
        }
      }
      checked = true;
    } catch (err) {
      if (err instanceof AppError) error = err;
    } finally {
      checking = false;
    }
  }

  async function signOut(): Promise<void> {
    await session.logout();
    router.navigate("/signed-out", { replace: true });
  }
</script>

<AuthLayout title="You're verified — welcome to EpiDev" subtitle={`Signed in as ${session.user?.email ?? ""}`}>
  <div class="stack">
    <p class="copy">
      We haven't found a purchase or organization linked to this email yet. Purchases are
      discovered automatically by your verified email — as soon as one matches, your
      portal appears here. There's nothing else to set up.
    </p>

    <ul class="hints">
      <li>Bought EpiDev with a different email? Sign out and verify that address instead.</li>
      <li>Joining a team? Ask an owner to send an invitation to this email.</li>
      <li>Have an enterprise claim link? Open it — you're already signed in.</li>
    </ul>

    {#if error}
      <Banner tone="error" title="We couldn't check right now" correlationId={error.correlationId}>
        {error.userMessage}
      </Banner>
    {:else if checked}
      <Banner tone="info" title="No purchases found yet" role="status">
        Still nothing linked to this email. If you purchased recently, it can take a few
        minutes to appear — try again shortly.
      </Banner>
    {/if}

    <Button fullWidth loading={checking} onclick={checkAgain}>Check again</Button>
    <Button variant="ghost" fullWidth onclick={signOut}>Sign out</Button>

    <p class="support">
      Expecting access that isn't showing up? Contact
      <a href="mailto:support@epidev.ai">support@epidev.ai</a> from this email address and
      we'll link it for you.
    </p>
  </div>
</AuthLayout>

<style>
  .copy {
    font-size: 14.5px;
    margin: 0;
  }
  .hints {
    margin: 0;
    padding-left: 18px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13.5px;
    color: var(--muted);
  }
  .support {
    font-size: 13px;
    color: var(--muted);
    margin: 0;
    text-align: center;
  }
  .support a {
    color: var(--accent);
  }
</style>
